import { useEffect, useState } from "react"

/**
 * Custom hook to manage dark mode.
 * @returns The current dark mode, a function to set the dark mode, and a function to toggle the dark mode
 */
export const useDarkMode = () => {

  const [darkMode, setDarkMode] = useState<"light" | "dark">("light");

  const customSetMode = (mode: "dark" | "light") => {
    if (mode === "dark") {
      document.documentElement.classList.add("dark");
      localStorage.theme = "dark";
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.theme = "light";
    }
    setDarkMode(mode);
  }

  const toggleDarkMode = () => {
    customSetMode(darkMode === "dark" ? "light" : "dark");
  }

  useEffect(() => {
    const darkDefault = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (darkDefault && !localStorage.theme) {
      customSetMode("dark");
    } else if (localStorage.theme === "dark") {
      customSetMode("dark");
    }
  }, [])

  // watch for changes on the document class list for dark mode
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") {
          const isDark = document.documentElement.classList.contains("dark");
          if (isDark) {
            localStorage.theme = "dark";
          } else {
            localStorage.theme = "light";
          }
          setDarkMode(isDark ? "dark" : "light");
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  return [darkMode, customSetMode, toggleDarkMode] as const;
}